// ─── PageAuditReport.jsx ──────────────────────────────────────────────────────
// Interactive, scrollable single-page GEO Page Audit Report.
// Implements the design handoff at design_handoff_geo_page_audit_report/.
// Props: { data: <audit result JSON>, lang }

import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "./LanguageContext";
import { scoreColor } from "../lib/scoreColor";

const CIRC = 314.159; // 2 * PI * 50

const SECTIONS = [
  { id: "uebersicht", navKey: "navOverview" },
  { id: "inhalt",      navKey: "navInhalt",    catKey: "contentQuality", subtitleKey: "subtitleInhalt" },
  { id: "autoritat",   navKey: "navAutoritat", catKey: "authority",      subtitleKey: "subtitleAutoritat" },
  { id: "technik",     navKey: "navTechnik",   catKey: "technicalSeo",   subtitleKey: "subtitleTechnik" },
  { id: "nutzer",      navKey: "navNutzer",    catKey: "engagement",     subtitleKey: "subtitleNutzer" },
];

const SECTION_BG = { uebersicht: "#FFFFFF", inhalt: "#F8FAFC", autoritat: "#FFFFFF", technik: "#F8FAFC", nutzer: "#FFFFFF" };

function Donut({ score, size, strokeWidth, scoreFontSize, denomFontSize, trackColor = "#F1F5F9" }) {
  const color = scoreColor(score || 0);
  const dashoffset = (CIRC * (1 - (score || 0) / 100)).toFixed(2);
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox="0 0 120 120">
        <circle cx="60" cy="60" r="50" fill="none" stroke={trackColor} strokeWidth="10" />
        <circle
          cx="60" cy="60" r="50" fill="none" stroke={color} strokeWidth="10"
          strokeLinecap="round" strokeDasharray={CIRC} strokeDashoffset={dashoffset}
          transform="rotate(-90 60 60)"
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: scoreFontSize, fontWeight: 700, color: "#0F172A", lineHeight: 1 }}>{score ?? "—"}</div>
        <div style={{ fontSize: denomFontSize, color: "#94A3B8", marginTop: 2 }}>/100</div>
      </div>
    </div>
  );
}

function MetricCard({ label, score, colorByScore, bg }) {
  const color = colorByScore ? scoreColor(score || 0) : "#0F172A";
  return (
    <div style={{ padding: 18, borderRadius: 14, background: bg, border: "1px solid #E2E8F0" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", marginBottom: 10 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color, marginBottom: 10 }}>{score ?? "—"}</div>
      <div style={{ height: 5, borderRadius: 999, background: "#F1F5F9", overflow: "hidden" }}>
        <div style={{ height: "100%", borderRadius: 999, background: scoreColor(score || 0), width: `${score || 0}%` }} />
      </div>
    </div>
  );
}

function InsightsRecs({ insights, recs, t }) {
  const recText = (r) => (typeof r === "string" ? r : r?.text || r?.title || "");
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
      <div style={{ padding: 24, borderRadius: 16, background: "#FFFFFF", border: "1px solid #E2E8F0" }}>
        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#0F172A", marginBottom: 16 }}>
          {t("report.erkenntnisse")}
        </div>
        {(insights || []).map((txt, i) => (
          <div key={i} style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <div style={{ color: "#94A3B8", fontWeight: 700, flexShrink: 0 }}>–</div>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: "#475569" }}>{txt}</div>
          </div>
        ))}
      </div>
      <div style={{ padding: 24, borderRadius: 16, background: "#EFF6FF", border: "1px solid #DBEAFE" }}>
        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#1D4ED8", marginBottom: 16 }}>
          {t("report.empfehlungen")}
        </div>
        {(recs || []).map((r, i) => (
          <div key={i} style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <div style={{ color: "#2563EB", fontWeight: 700, flexShrink: 0 }}>›</div>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: "#1E3A5F" }}>{recText(r)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PageAuditReport({ data, lang: langProp }) {
  const langCtx = useLanguage();
  const t = langCtx?.t;
  const lang = langProp || langCtx?.lang || "de";
  const [active, setActive] = useState("uebersicht");
  const observerRef = useRef(null);

  useEffect(() => {
    const ids = SECTIONS.map((s) => s.id);
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, [data]);

  if (!data) return null;

  const overallScore = data.overallScore;
  const categories = data.categories || {};
  const structural = data.structural || {};
  const domain = (() => {
    try { return new URL(data.url).hostname; } catch { return data.url || ""; }
  })();
  const auditDateStr = data.auditDate ? new Date(data.auditDate).toLocaleDateString(lang === "de" ? "de-CH" : lang) : "";

  const checklist = [
    { label: t("report.checkTitle"), ok: !!structural.hasTitle },
    { label: t("report.checkMetaDesc"), ok: !!structural.hasMetaDescription },
    { label: t("report.checkH1"), ok: structural.h1Count === 1 },
    { label: t("report.checkAltText"), ok: structural.imagesWithoutAlt === 0 },
    { label: t("report.checkContact"), ok: !!structural.hasContactForm },
    { label: t("report.checkSocial"), ok: !!structural.hasSocialLinks },
  ];

  const technik = categories.technicalSeo || {};
  const vitals = technik.vitals;
  const vitalsLabel = vitals == null
    ? t("report.vitalsUnknown")
    : vitals.passed ? t("report.vitalsPassed") : t("report.vitalsFailed");
  const vitalsBg = vitals == null ? "#F8FAFC" : vitals.passed ? "#F0FDF4" : "#FEF2F2";
  const vitalsBorder = vitals == null ? "#E2E8F0" : vitals.passed ? "#BBF7D0" : "#FECACA";
  const vitalsTextColor = vitals == null ? "#64748B" : vitals.passed ? "#16A34A" : "#DC2626";

  return (
    <div style={{ fontFamily: "Helvetica, Arial, sans-serif", background: "#FFFFFF", color: "#0F172A" }}>
      <style>{`html { scroll-behavior: smooth; }`}</style>

      {/* Sticky nav */}
      <div style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 40px", height: 64 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, background: "#2563EB", flexShrink: 0 }} />
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A", letterSpacing: "-0.01em", whiteSpace: "nowrap" }}>GEO Page Audit Report</div>
          <div style={{ fontSize: 13, color: "#94A3B8", paddingLeft: 8, borderLeft: "1px solid #E2E8F0", marginLeft: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{domain}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {SECTIONS.map((s) => (
            <a key={s.id} href={`#${s.id}`} style={{
              textDecoration: "none", fontSize: 13, fontWeight: 600, padding: "8px 14px", borderRadius: 8,
              color: active === s.id ? "#2563EB" : "#64748B",
              background: active === s.id ? "#EFF6FF" : "transparent",
              transition: "color 0.15s, background 0.15s",
            }}>{t(`report.${s.navKey}`)}</a>
          ))}
        </div>
      </div>

      {/* ================= ÜBERSICHT ================= */}
      <section id="uebersicht" style={{ minHeight: "100vh", padding: "72px 40px 88px", display: "flex", flexDirection: "column", alignItems: "center", boxSizing: "border-box" }}>
        <div style={{ width: "100%", maxWidth: 920 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563EB", marginBottom: 10 }}>
            {t("report.eyebrowOverview")}
          </div>
          <div style={{ fontSize: 13, color: "#94A3B8", marginBottom: 40 }}>
            {t("report.metaLabel")} &nbsp;·&nbsp; {domain} {auditDateStr && <>&nbsp;·&nbsp; {auditDateStr}</>}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 48, paddingBottom: 44, marginBottom: 44, borderBottom: "1px solid #E2E8F0", flexWrap: "wrap" }}>
            <Donut score={overallScore} size={184} scoreFontSize={34} denomFontSize={11} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A", marginBottom: 10 }}>{t("report.scoreLabel")}</div>
              <div style={{ fontSize: 15, lineHeight: 1.6, color: "#475569", maxWidth: 560 }}>{data.summary}</div>
            </div>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 56 }}>
            {checklist.map((item, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 999,
                background: item.ok ? "#F0FDF4" : "#FEF2F2", border: `1px solid ${item.ok ? "#BBF7D0" : "#FECACA"}`,
              }}>
                <div style={{ width: 16, height: 16, borderRadius: "50%", background: item.ok ? "#16A34A" : "#DC2626", color: "#FFFFFF", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {item.ok ? "✓" : "✕"}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{item.label}</div>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94A3B8", marginBottom: 16 }}>
            {t("report.areasEyebrow")}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
            {SECTIONS.filter((s) => s.catKey).map((s) => {
              const cat = categories[s.catKey] || {};
              const color = scoreColor(cat.score || 0);
              return (
                <a key={s.id} href={`#${s.id}`} style={{
                  textDecoration: "none", display: "block", padding: "24px 20px", borderRadius: 16,
                  border: "1px solid #E2E8F0", background: "#FFFFFF",
                }}>
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#94A3B8", marginBottom: 12 }}>
                    {t(`report.${s.navKey}`)}
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 14 }}>
                    <div style={{ fontSize: 32, fontWeight: 700, color }}>{cat.score ?? "—"}</div>
                    <div style={{ fontSize: 13, color: "#94A3B8" }}>/100</div>
                  </div>
                  <div style={{ height: 6, borderRadius: 999, background: "#F1F5F9", overflow: "hidden" }}>
                    <div style={{ height: "100%", borderRadius: 999, background: color, width: `${cat.score || 0}%` }} />
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= 4 DETAIL SECTIONS ================= */}
      {SECTIONS.filter((s) => s.catKey).map((s, idx) => {
        const cat = categories[s.catKey] || {};
        const isTechnik = s.id === "technik";
        const cardBg = SECTION_BG[s.id] === "#FFFFFF" ? "#F8FAFC" : "#FFFFFF";
        const gridCols = s.id === "nutzer" ? 5 : 4;

        return (
          <section key={s.id} id={s.id} style={{
            minHeight: "100vh", padding: s.id === "nutzer" ? "72px 40px 100px" : "72px 40px 88px",
            display: "flex", flexDirection: "column", alignItems: "center", boxSizing: "border-box",
            background: SECTION_BG[s.id], borderTop: "1px solid #E2E8F0",
          }}>
            <div style={{ width: "100%", maxWidth: 920 }}>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2563EB", marginBottom: 10 }}>
                {t("report.areaLabel")} {idx + 1} / 4
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: isTechnik ? "space-between" : "flex-start", marginBottom: isTechnik ? 40 : 48, flexWrap: "wrap", gap: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 32 }}>
                  <Donut score={cat.score} size={140} scoreFontSize={26} denomFontSize={10} trackColor="#E2E8F0" />
                  <div>
                    <div style={{ fontSize: 40, fontWeight: 700, color: "#0F172A", letterSpacing: "-0.02em" }}>{t(`report.${s.navKey}`)}</div>
                    <div style={{ fontSize: 15, color: "#64748B", marginTop: 6 }}>{t(`report.${s.subtitleKey}`)}</div>
                  </div>
                </div>
                {isTechnik && technik.psiReportUrl && (
                  <a href={technik.psiReportUrl} target="_blank" rel="noopener noreferrer" title={t("report.psiButtonHint")} style={{
                    textDecoration: "none", flexShrink: 0, display: "flex", alignItems: "center", gap: 8,
                    padding: "12px 18px", borderRadius: 10, background: "#0F172A", color: "#FFFFFF",
                    fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
                  }}>{t("report.psiButton")} <span>↗</span></a>
                )}
              </div>

              {isTechnik ? (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
                    <MetricCard label={t("report.metricLeistung")} score={cat.breakdown?.leistung} colorByScore bg="#FFFFFF" />
                    <MetricCard label={t("report.metricBarrierefreiheit")} score={cat.breakdown?.barrierefreiheit} colorByScore bg="#FFFFFF" />
                    <MetricCard label={t("report.metricBestPractices")} score={cat.breakdown?.bestPractices} colorByScore bg="#FFFFFF" />
                    <MetricCard label={t("report.metricSeo")} score={cat.breakdown?.seo} colorByScore bg="#FFFFFF" />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 24, padding: "16px 20px", borderRadius: 14, background: vitalsBg, border: `1px solid ${vitalsBorder}`, marginBottom: 48, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: vitalsTextColor, whiteSpace: "nowrap" }}>{vitalsLabel}</div>
                    {vitals && (
                      <>
                        <div style={{ width: 1, height: 20, background: vitalsBorder }} />
                        {[{ label: "LCP", value: vitals.lcp }, { label: "INP", value: vitals.inp }, { label: "CLS", value: vitals.cls }].map((v) => (
                          <div key={v.label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                            <span style={{ color: "#64748B", fontWeight: 600 }}>{v.label}</span>
                            <span style={{ color: "#0F172A", fontWeight: 700 }}>{v.value ?? "—"}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: `repeat(${gridCols}, 1fr)`, gap: s.id === "nutzer" ? 14 : 16, marginBottom: 48 }}>
                  {Object.entries(cat.breakdown || {}).map(([key, val]) => (
                    <MetricCard
                      key={key}
                      label={key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase())}
                      score={val}
                      bg={cardBg}
                    />
                  ))}
                </div>
              )}

              <InsightsRecs insights={cat.findings} recs={cat.recommendations} t={t} />
            </div>

            {s.id === "nutzer" && (
              <div style={{ width: "100%", maxWidth: 920, textAlign: "center", fontSize: 12, color: "#94A3B8", paddingTop: 24, marginTop: 64, borderTop: "1px solid #E2E8F0" }}>
                {t("report.footer")} – {domain} {auditDateStr && `– ${auditDateStr}`}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
